import { useGetFrontRichiesteIdPreventivi } from '@framework/api-client/api/inquiries-compile/preventivi-richiesta/preventivi-richiesta.ts';
import { usePostFrontRichiesteIdAzioniIntegra } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { TitleSubmissionRow } from '@framework/common-web/ui/inquiry/submission/TitleSubmissionRow.tsx';
import { SubmissionBurdenLogic } from '@framework/frontoffice-submission/burden/SubmissionBurdenLogic.ts';
import { DelegationMachine } from '@framework/frontoffice-submission/module/delegation/DelegationMachine.ts';
import { useSubmission } from '@framework/frontoffice-submission/submission/hooks/useSubmission.ts';
import { useSubmissionMenuItems } from '@framework/frontoffice-submission/submission/hooks/useSubmissionMenuItems.tsx';
import { useSubmissionModules } from '@framework/frontoffice-submission/submission/hooks/useSubmissionModules.ts';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import { SubmissionStateTag } from '@framework/frontoffice-submission/submission/state/SubmissionStateTag.tsx';
import { useGenerateAutomaticBurdenPayments } from '@framework/frontoffice-submission/utils/useGenerateAutomaticBurdenPayments.ts';
import { LinkButton } from '@framework/ui/components/button/LinkButton.tsx';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import {
  type BrandedPath,
  generatePath,
  useHistory,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { useIdFromUrl } from '@framework/ui/hooks/useIdFromUrl.ts';
import { Alert, message, Space, Typography } from 'antd';
import axios from 'axios';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps } from 'react-router';
import { useTheme } from 'styled-components';
import { getSubmissionMenuLink } from '../../components/submissions/hooks/getSubmissionMenuLink.ts';
import { PageLayout } from '../../ui/layout/PageLayout.tsx';
import { LoadingPage } from '../loading/LoadingPage.tsx';
import { ContentCardWrapper } from './components/ContentCardWrapper.tsx';
import { IntegrationConfirmButton } from './components/IntegrationConfirmButton.tsx';
import { SubmissionCloneButton } from './components/SubmissionDuplicateButton.tsx';
import { SubmissionLinkedBox } from './components/SubmissionLinkedBox.tsx';
import { SubmissionUndoButton } from './components/SubmissionUndoButton.tsx';
import { useDelegationData } from './delegation/hooks/useDelegationData.ts';
import { getSubmissionSentTabList } from './sent/getSubmissionSentTabList.tsx';
import { IntegrationBanner } from './sent/IntegrationBanner.tsx';
import { SubmissionSentTabCommunications } from './sent/SubmissionSentTabCommunications.tsx';
import { SubmissionSentTabIntegrations } from './sent/SubmissionSentTabIntegrations.tsx';
import { SubmissionSentTabPayments } from './sent/SubmissionSentTabPayments.tsx';
import { SubmissionSentTabReceipt } from './sent/SubmissionSentTabReceipt.tsx';
import { SubmissionSentTabRequest } from './sent/SubmissionSentTabRequest.tsx';
import { useSubmissionsIntegrationsState } from './sent/useSubmissionsIntegrationsState.tsx';

const { Text, Title } = Typography;

interface Params {
  id: string;
  moduleId: string;
}

export type ISubmissionSentPageProps = RouteComponentProps<Params>;

/**
 * Pagina della richiesta dopo l'invio.
 */
export function SubmissionSentPage(props: ISubmissionSentPageProps) {
  const [submissionId] = useIdFromUrl(props, 'id');
  const [submissionModuleId] = useIdFromUrl(props, 'moduleId');

  const history = useHistory();
  const { t } = useTranslation();

  const submissionData = useSubmission(submissionId);
  const currentUser = useCurrentUser();

  const submissionModules = useSubmissionModules({ submissionId });

  // Recupero i preventivi collegati alla richiesta
  const quotesResult = useGetFrontRichiesteIdPreventivi(
    submissionId ?? '<unknown>',
    { query: { enabled: Boolean(submissionId) } },
  );

  const submissionMenuItems = useSubmissionMenuItems({
    modules: submissionModules.modules,
    submissionId,
    submission: submissionData.submission,
    getMenuLink: getSubmissionMenuLink,
  });

  const { delegationData } = useDelegationData({
    modules: submissionModules.modules,
    applicant: submissionData?.submission?.datiTitolari,
    submissionId,
  });

  const { submission, inquiry, title, submissionRequest, inquiryRequest } =
    submissionData;

  const startIntegration = usePostFrontRichiesteIdAzioniIntegra();

  // Breadcrumbs
  useBreadcrumbItem({
    path: '/richieste',
    title: t('submission-sent-form.richieste', 'Richieste'),
  });
  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title: inquiryRequest.data?.titolo ?? '',
  });

  const [activeTab, setActiveTab] = useState<string>('receipt');
  const onTabChange = (key: string) => {
    setActiveTab(key);
  };

  const isLoading =
    submissionRequest.isLoading ||
    inquiryRequest.isLoading ||
    submissionModules.isLoading ||
    quotesResult.isLoading;
  const error =
    submissionRequest.error ?? inquiryRequest.error ?? submissionModules.error;

  const sentLayout = useMemo(
    () => inquiryRequest.data?.postCompilazione?.inviato,
    [inquiryRequest.data?.postCompilazione?.inviato],
  );
  const theme = useTheme();

  const shouldShowCommunicationsTab = SubmissionLogic.canShowCommunications(
    submission?.stato,
  );

  const shouldShowPaymentsTab = SubmissionBurdenLogic.showPayments(
    submission?.statoOneri,
    submission?.statoPreventivi,
  );

  // Gestione delle integrazioni
  const {
    isIntegrated,
    canIntegrate,
    hasIntegrations,
    hasIntegrationDrafts,
    shouldShowIntegrationsTab,
    draftIntegrationId,
  } = useSubmissionsIntegrationsState(inquiry, submission);

  const handleStartIntegration = useCallback(async () => {
    try {
      const result = await startIntegration.mutateAsync({
        id: submission!.id!,
      });

      message.success(
        t(
          'submission-sent-form.integrazione-creata-con-successo',
          `Integrazione creata con successo`,
        ),
      );
      history.push(
        generatePath(`/richieste/:id`, {
          id: result.id!,
        }),
      );
    } catch (e) {
      console.error(e);
      // Il BE risponde 409 quando la richiesta è congelata da un
      // annullamento in corso: in quel caso mostriamo direttamente il
      // messaggio dedicato
      const errorMessage =
        axios.isAxiosError(e) && e.response?.status === 409
          ? t(
              'submission-sent-form.integrazione-non-consentita-annullamento',
              "Operazione non consentita: è in corso l'annullamento della richiesta.",
            )
          : getNetworkErrorMessage(
              e,
              t(
                'submission-sent-form.errore-in-integrazione-richiesta',
                `Si è verificato un errore durante l'integrazione della richiesta, si prega di riprovare.`,
              ),
            );
      message.error(errorMessage);
    }
  }, [startIntegration, submission, t, history]);

  const handleConfirmIntegration = useCallback(() => {
    handleStartIntegration();
  }, [handleStartIntegration]);

  // Richiamo il custom hook per la generazione automatica dei pagamenti dopo l'invio
  useGenerateAutomaticBurdenPayments('afterSent', submissionId);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <PageLayout>
        <Alert
          showIcon
          type="error"
          message={t(
            'submission-sent-form.errore-in-caricamento-domanda',
            'Si è verificato un errore durante il caricamento della domanda.',
          )}
          description={getNetworkErrorMessage(
            error,
            t(
              'submission-sent-form.errore-in-caricamento-richiesta',
              'Si è verificato un errore durante il caricamento della richiesta.',
            ),
          )}
        />
      </PageLayout>
    );
  }

  if (!submissionId) {
    return (
      <PageLayout>
        <Alert
          showIcon
          type="error"
          message={t(
            'submission-sent-form.errore-in-caricamento-domanda',
            'Si è verificato un errore durante il caricamento della domanda.',
          )}
          description={t(
            'submission-sent-form.id-della-richiesta-non-valido',
            'ID della richiesta non valido',
          )}
        />
      </PageLayout>
    );
  }

  // in caso di richiesta non inviata
  if (!SubmissionLogic.isSubmissionSent(submission) && submissionId) {
    history.replace(generatePath('/richieste/:id', { id: submissionId }));
  }

  const showCloneButton =
    !inquiryRequest.data?.flRichiestaUnivoca &&
    DelegationMachine.can(delegationData, '.canDuplicate', currentUser).allowed;

  return (
    <PageLayout>
      <SubmissionLinkedBox submission={submission} inquiry={inquiry} />
      <ContentCardWrapper submission={submission}>
        {SubmissionLogic.isSubmissionUndoInProgress(submission) && (
          <Alert
            showIcon
            type="info"
            style={{ marginBottom: 16 }}
            message={t(
              'submission-sent-form.annullamento-in-corso',
              "È in corso l'annullamento della richiesta. Le operazioni di modifica, integrazione, delega e invio comunicazioni sono sospese fino alla conclusione.",
            )}
          />
        )}
        <ResponsiveCard
          hideCardHeader
          title={
            <TitleSubmissionRow
              stateTag={
                <Space>
                  <SubmissionStateTag submission={submission} />
                  {submission?.numeroProcedimento && (
                    <Space>
                      <Text style={{ fontWeight: 'normal' }}>
                        {t(
                          'submission-sent-form.procedimento',
                          'Procedimento:',
                        )}{' '}
                      </Text>
                      <Text>{submission?.numeroProcedimento}</Text>
                    </Space>
                  )}
                </Space>
              }
              right={
                <Space>
                  {SubmissionLogic.canUndo(submission) && (
                    <SubmissionUndoButton
                      submissionId={submission?.id}
                      size="large"
                    />
                  )}
                  {showCloneButton && (
                    <SubmissionCloneButton
                      isJustCreated={false}
                      submissionId={submissionId}
                    />
                  )}
                  {canIntegrate && (
                    <IntegrationConfirmButton
                      onConfirm={handleConfirmIntegration}
                    />
                  )}
                  {hasIntegrationDrafts && draftIntegrationId && (
                    <LinkButton
                      type="primary"
                      size="large"
                      to={generatePath('/richieste/:id', {
                        id: draftIntegrationId,
                      })}
                    >
                      {t(
                        'submission-sent-form.apri-integrazione-in-bozza',
                        'Apri Integrazione in Bozza',
                      )}
                    </LinkButton>
                  )}
                </Space>
              }
            >
              {title}
            </TitleSubmissionRow>
          }
          cover={
            hasIntegrations ? (
              <IntegrationBanner
                integrationID={null}
                message={
                  isIntegrated
                    ? t(
                        'submission-sent-form.richiesta-integrata',
                        'Questa richiesta è stata integrata.',
                      )
                    : hasIntegrationDrafts
                      ? t(
                          'submission-sent-form.integrazione-in-bozza',
                          'È presente una bozza di integrazione per questa richiesta.',
                        )
                      : t(
                          'submission-sent-form.integrazione-di-un-altra-richiesta',
                          "Questa è un'integrazione di un'altra richiesta.",
                        )
                }
                handleShowIntegrations={
                  activeTab !== 'integrations'
                    ? () => onTabChange('integrations')
                    : undefined
                }
              />
            ) : null
          }
          tabList={getSubmissionSentTabList({
            shouldShowCommunicationsTab,
            shouldShowIntegrationsTab,
            shouldShowPaymentsTab,
            submissionId: submission?.id,
          })}
          activeTabKey={activeTab}
          onTabChange={onTabChange}
        >
          {/* Ricevuta Invio */}
          <SubmissionSentTabReceipt
            active={activeTab === 'receipt'}
            submission={submission}
            sentLayout={sentLayout}
          />
          {/* Richiesta in modalità read only */}
          <SubmissionSentTabRequest
            active={activeTab === 'request'}
            menuItems={submissionMenuItems.menuItems}
            initialModuleId={submissionModules.modules?.[0]?.id}
            submissionModuleId={submissionModuleId}
            submissionModules={submissionModules.modules}
            submission={submission}
            inquiryType={inquiry}
          />
          {/* Comunicazioni */}
          {shouldShowCommunicationsTab && (
            <SubmissionSentTabCommunications
              active={activeTab === 'communications'}
              submission={submission}
            />
          )}
          {/* Integrazioni */}
          {shouldShowIntegrationsTab && (
            <SubmissionSentTabIntegrations
              active={activeTab === 'integrations'}
              submission={submission}
            />
          )}
          {/* Pagamenti */}
          {shouldShowPaymentsTab && (
            <SubmissionSentTabPayments
              active={activeTab === 'payments'}
              submission={submission}
            />
          )}
        </ResponsiveCard>
      </ContentCardWrapper>
    </PageLayout>
  );
}
